import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiDollarSign,
  FiShoppingCart,
  FiUsers,
  FiTrendingUp,
  FiRefreshCcw,
  FiPackage,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';

const COLORS = ["#7A2238", "#D4AF37", "#5A182A", "#A94D6D", "#2563EB"];

function indiaDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function getRange(range) {
  const today = indiaDate();
  const current = new Date(`${today}T00:00:00.000Z`);

  if (range === "Today") {
    return { from: today, to: today };
  }

  if (range === "This Week") {
    const weekStart = new Date(current);
    const day = weekStart.getUTCDay();
    const daysSinceMonday = (day + 6) % 7;

    weekStart.setUTCDate(weekStart.getUTCDate() - daysSinceMonday);

    return {
      from: weekStart.toISOString().slice(0, 10),
      to: today,
    };
  }

  if (range === "This Year") {
    return {
      from: `${current.getUTCFullYear()}-01-01`,
      to: today,
    };
  }

  return {
    from: `${today.slice(0, 7)}-01`,
    to: today,
  };
}

function formatChartLabel(value) {
  if (!value) return "";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
  }).format(new Date(`${value}T00:00:00.000Z`));
}

function comparisonMeta(value, { inverse = false } = {}) {
  if (value === null || value === undefined) {
    return {
      label: "No comparable data last year",
      className: "bg-gray-100 text-gray-500",
    };
  }

  const percentage = Number(value);
  const positive = inverse ? percentage <= 0 : percentage >= 0;

  return {
    label: `${percentage >= 0 ? "+" : ""}${percentage.toFixed(1)}% vs last year`,
    className: positive
      ? "bg-green-100 text-green-700"
      : "bg-red-100 text-red-700",
  };
}

function EmptyState({ text }) {
  return (
    <div className="flex h-[280px] items-center justify-center text-sm text-gray-400">
      {text}
    </div>
  );
}

export default function SalesReport() {
  const { accessToken } = useAuth();

  const [dateRange, setDateRange] = useState("This Month");
  const [storeId, setStoreId] = useState("");
  const [filters,setFilters] = useState({});
  const [options,setOptions] = useState({});
  const [from,setFrom] = useState(indiaDate());
  const [to,setTo] = useState(indiaDate());

  const [dashboard, setDashboard] = useState(null);
  const [stores, setStores] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");

  const selectedRange = useMemo(() => dateRange === 'Custom' ? {from,to} : getRange(dateRange), [dateRange,from,to]);

  const loadStores = useCallback(async () => {
    if (!accessToken) return;

    try {
      const response = await apiRequest('/reports/dashboard/filters',{accessToken});
      setStores((response.stores || []).map(store=>({...store,isActive:true})));
      setOptions(response);
    } catch {
      setStores([]);
    }
  }, [accessToken]);

  const loadReport = useCallback(
    async (showRefresh = false) => {
      if (!accessToken) return;

      try {
        if (showRefresh) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }

        setError("");

        const query = new URLSearchParams({
          from: selectedRange.from,
          to: selectedRange.to,
        });

        if (storeId) {
          query.set("storeId", storeId);
        }
        Object.entries(filters).forEach(([key,value])=>{if(value)query.set(key,value);});

        const response = await apiRequest(
          `/reports/sales?${query.toString()}`,
          { accessToken },
        );

        setDashboard(response);
      } catch (requestError) {
        setError(requestError.message || "Unable to load sales report.");
        setDashboard(null);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [accessToken, selectedRange.from, selectedRange.to, storeId,filters],
  );

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const exportReport = () => {
    if (!dashboard) return;

    const metrics = dashboard.metrics || {};
    const comparison = dashboard.comparison || {};

    const rows = [
      ["Sales Report", "Value"],
      ["Period", `${selectedRange.from} to ${selectedRange.to}`],
      [
        "Last Year Period",
        comparison.previousPeriod
          ? `${comparison.previousPeriod.from} to ${comparison.previousPeriod.to}`
          : "—",
      ],
      ["Total Sales", metrics.revenue],
      ["Orders", metrics.orders],
      ["Customers", metrics.customers],
      ["Average Order", metrics.averageOrder],
      ["Returns", metrics.returns],
      ["Last Year Sales", comparison.previous?.revenue],
      ["Sales Change %", comparison.changes?.revenue],
      ["Last Year Orders", comparison.previous?.orders],
      ["Orders Change %", comparison.changes?.orders],
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = `sales-report-${selectedRange.from}-to-${selectedRange.to}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const metrics = dashboard?.metrics || {};
  const changes = dashboard?.comparison?.changes || {};

  const salesTrend = dashboard?.salesTrend || [];
  const previousSalesTrend = dashboard?.previousSalesTrend || [];

  const comparisonTrend = useMemo(() => {
    const previousByIndex = new Map(
      previousSalesTrend.map((item, index) => [index, item]),
    );

    return salesTrend.map((item, index) => ({
      label: formatChartLabel(item.date),
      currentSales: Number(item.sales || 0),
      lastYearSales: Number(previousByIndex.get(index)?.sales || 0),
    }));
  }, [salesTrend, previousSalesTrend]);

  const stats = [
    {
      title: "Total Sales",
      value: formatMoney(metrics.revenue),
      comparison: comparisonMeta(changes.revenue),
      color: "bg-green-500",
      icon: <FiDollarSign size={24} />,
    },
    {
      title: "Orders",
      value: formatNumber(metrics.orders),
      comparison: comparisonMeta(changes.orders),
      color: "bg-blue-500",
      icon: <FiShoppingCart size={24} />,
    },
    {
      title: "Customers",
      value: formatNumber(metrics.customers),
      comparison: comparisonMeta(changes.customers),
      color: "bg-purple-500",
      icon: <FiUsers size={24} />,
    },
    {
      title: "Average Order",
      value: formatMoney(metrics.averageOrder),
      comparison: comparisonMeta(changes.averageOrder),
      color: "bg-orange-500",
      icon: <FiTrendingUp size={24} />,
    },
    {
      title: "Returns",
      value: formatNumber(metrics.returns),
      comparison: comparisonMeta(changes.returns, { inverse: true }),
      color: "bg-red-500",
      icon: <FiRefreshCcw size={24} />,
    },
    {
      title: "Products Sold",
      value: formatNumber(metrics.productsSold),
      comparison: comparisonMeta(changes.productsSold),
      color: "bg-emerald-500",
      icon: <FiPackage size={24} />,
    },
  ];

  const categorySales = dashboard?.categorySales || [];
  const paymentMethods = dashboard?.paymentMethods || [];
  const topProducts = dashboard?.topProducts || [];
  const topCustomers = dashboard?.topCustomers || [];
  const recentTransactions = dashboard?.recentTransactions || [];
  const storeSales = dashboard?.storeSales || [];

  return (
    <div className="charms-analytics min-h-screen space-y-6">
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-primary-800">Sales Analytics</h1>

          <p className="mt-2 text-gray-500">
            POS receipts and sales-order performance, with delivery progress and product insights.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => loadReport(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-3 shadow-sm disabled:opacity-50"
          >
            <FiRefreshCcw className={isRefreshing ? "animate-spin" : ""} />
            Refresh
          </button>

        </div>
      </div>

      {error ? (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="mb-8 rounded-3xl bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium">Date Range</label>

            <select
              value={dateRange}
              onChange={(event) => setDateRange(event.target.value)}
              className="w-full rounded-xl border border-gray-200 p-3"
            >
              <option>Today</option>
              <option>This Week</option>
              <option>This Month</option>
              <option>This Year</option>
              <option>Custom</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Store</label>

            <select
              value={storeId}
              onChange={(event) => setStoreId(event.target.value)}
              className="w-full rounded-xl border border-gray-200 p-3"
            >
              <option value="">All Stores</option>

              {stores
                .filter((store) => store.isActive)
                .map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name} ({store.code})
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-4">{[['brandId','Brand','brands'],['categoryId','Category','categories'],['subCategoryId','Sub category','subCategories'],['seasonId','Season','seasons'],['productId','Product','products'],['colorId','Colour','colors'],['sizeId','Size','sizes']].map(([key,label,source])=><label key={key} className="text-sm text-gray-600">{label}<select className="mt-1 w-full rounded-xl border p-3" value={filters[key]||''} onChange={e=>setFilters({...filters,[key]:e.target.value})}><option value="">All {label}</option>{(options[source]||[]).map(item=><option value={item.id} key={item.id}>{item.name}</option>)}</select></label>)}</div>
        {dateRange === 'Custom' && <div className="mt-4 flex gap-3"><input aria-label="From date" type="date" value={from} max={to} onChange={e=>setFrom(e.target.value)} className="rounded-xl border p-3"/><input aria-label="To date" type="date" value={to} min={from} onChange={e=>setTo(e.target.value)} className="rounded-xl border p-3"/></div>}
        <button type="button" className="mt-3 rounded-xl border px-4 py-2 text-primary-800" onClick={()=>{setFilters({});setStoreId('');}}>Reset filters</button>
        <p className="mt-5 text-xs text-gray-400">
          Current: {selectedRange.from} to {selectedRange.to}
          {dashboard?.comparison?.previousPeriod
            ? ` · Last year: ${dashboard.comparison.previousPeriod.from} to ${dashboard.comparison.previousPeriod.to}`
            : ""}
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-3xl bg-white py-24 text-center text-gray-500 shadow-sm">
          Loading live sales report...
        </div>
      ) : dashboard ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {[...stats.map(item=>({...item,title:'POS · '+item.title})), ...[
              ['Orders created',dashboard.salesOrders?.metrics.orders,false],['Confirmed order value',dashboard.salesOrders?.metrics.confirmedValue,true],['Average confirmed order',dashboard.salesOrders?.metrics.averageOrder,true],['Draft orders',dashboard.salesOrders?.metrics.drafts,false],['Cancelled orders',dashboard.salesOrders?.metrics.cancelled,false],['Ordered units',dashboard.salesOrders?.metrics.orderedQuantity,false],['Delivered units',dashboard.salesOrders?.metrics.deliveredQuantity,false],['Pending delivery units',dashboard.salesOrders?.metrics.pendingQuantity,false],['Order GST',dashboard.salesOrders?.metrics.gst,true],
            ].map(([title,value,currency])=>({title:'Sales Orders · '+title,value:currency?formatMoney(value):formatNumber(value),icon:<FiShoppingCart size={24}/>,color:'bg-primary-800',comparison:{label:'Selected period',className:'text-gray-500 bg-gray-50'}}))].map((item) => (
              <div
                key={item.title}
                className="analytics-kpi"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{item.title}</p>

                    <h2 className="mt-3 text-3xl font-bold">{item.value}</h2>

                    <span
                      className={`mt-2 inline-block rounded-full px-3 py-1 text-sm ${item.comparison.className}`}
                    >
                      {item.comparison.label}
                    </span>
                  </div>

                  <div
                    className="analytics-icon flex shrink-0 items-center justify-center"
                  >
                    {item.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="xl:col-span-12 grid gap-5 lg:grid-cols-2">
              { [['Sales order trend','trend'],['Sales order status','statuses'],['Sales order value by store','stores'],['Leading sales-order products','products']].map(([label,key])=><section key={key} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5"><h2 className="mb-5 font-semibold text-primary-800">{label}</h2>{dashboard.salesOrders?.[key]?.length ? <ResponsiveContainer width="100%" height={300}>{key==='trend'?<LineChart data={dashboard.salesOrders[key]}><CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="name" tick={{fontSize:11}}/><YAxis/><Tooltip formatter={value=>formatMoney(value)}/><Line dataKey="value" stroke="#7A2238" strokeWidth={3}/></LineChart>:<BarChart data={dashboard.salesOrders[key]} layout="vertical"><CartesianGrid strokeDasharray="3 3"/><XAxis type="number"/><YAxis dataKey="name" type="category" width={120} tick={{fontSize:11}}/><Tooltip formatter={value=>key==='statuses'?formatNumber(value):formatMoney(value)}/><Bar dataKey="value" fill="#7A2238" maxBarSize={24}/></BarChart>}</ResponsiveContainer>:<EmptyState text="No sales orders match these filters."/>}</section>)}
            </div>
            <section className="xl:col-span-12 rounded-2xl border border-slate-200 bg-white overflow-hidden"><div className="p-5"><h2 className="font-semibold text-primary-800">Recent sales orders</h2><p className="text-xs text-gray-500 mt-1">Confirmed order values include tax. Drafts and cancelled orders are excluded from confirmed value metrics.</p></div><div className="overflow-auto"><table className="w-full text-sm"><thead className="bg-primary-800 text-white"><tr>{['Order','Date','Customer','Status','Value'].map(h=><th className="p-3 text-left border border-primary-700" key={h}>{h}</th>)}</tr></thead><tbody>{(dashboard.salesOrders?.recent||[]).map(o=><tr key={o.id}>{[o.number,new Date(o.date).toLocaleDateString('en-IN'),o.customer,o.status.replaceAll('_',' '),formatMoney(o.value)].map((v,i)=><td key={i} className="p-3 border border-gray-200">{v}</td>)}</tr>)}</tbody></table>{!dashboard.salesOrders?.recent?.length&&<EmptyState text="No matching sales orders."/>}</div></section>
            <section className="xl:col-span-8 rounded-3xl bg-white p-6 shadow-sm">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-primary-800">
                  Sales Trend Comparison
                </h2>

                <p className="text-gray-500">
                  Current-period sales versus the equivalent period last year.
                </p>
              </div>

              {comparisonTrend.length ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={comparisonTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Legend />

                    <Line
                      type="monotone"
                      dataKey="currentSales"
                      name="This Year"
                      stroke="#7A2238"
                      strokeWidth={4}
                    />

                    <Line
                      type="monotone"
                      dataKey="lastYearSales"
                      name="Last Year"
                      stroke="#D4AF37"
                      strokeWidth={3}
                      strokeDasharray="7 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No sales trend data for this period." />
              )}
            </section>

            <section className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Payment Methods
              </h2>

              {paymentMethods.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={paymentMethods}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                    >
                      {paymentMethods.map((entry, index) => (
                        <Cell
                          key={`${entry.name}-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Legend />
                    <Tooltip formatter={(value) => formatMoney(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No payment data available." />
              )}
            </section>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Sales by Store
              </h2>

              {storeSales.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={storeSales}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Bar
                      dataKey="value"
                      name="Revenue"
                      fill="#7A2238"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="Store sales data will appear here." />
              )}
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Sales by Category
              </h2>

              {categorySales.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={categorySales}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={100}
                    >
                      {categorySales.map((entry, index) => (
                        <Cell
                          key={`${entry.name}-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState text="No category sales data available." />
              )}
            </section>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Top Selling Products
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[550px]">
                  <thead>
                    <tr className="border-b text-left text-sm text-gray-500">
                      <th className="py-3">Product</th>
                      <th className="text-center">Qty</th>
                      <th className="text-center">Revenue</th>
                    </tr>
                  </thead>

                  <tbody>
                    {topProducts.map((product) => (
                      <tr
                        key={product.variantId}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="py-4">
                          <h3 className="font-semibold">{product.name}</h3>
                          <p className="text-sm text-gray-500">{product.sku}</p>
                        </td>

                        <td className="text-center">
                          {formatNumber(product.sold)}
                        </td>

                        <td className="text-center font-semibold text-primary-800">
                          {formatMoney(product.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Top Customers
              </h2>

              <div className="space-y-4">
                {topCustomers.length ? (
                  topCustomers.map((customer) => (
                    <div
                      key={customer.name}
                      className="rounded-2xl bg-gray-50 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{customer.name}</h3>
                          <p className="text-sm text-gray-500">
                            {customer.orders} Orders
                          </p>
                        </div>

                        <span className="font-bold text-primary-800">
                          {formatMoney(customer.spent)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="py-10 text-center text-sm text-gray-400">
                    No customer sales found.
                  </p>
                )}
              </div>
            </section>
          </div>

          <section className="mt-6 rounded-3xl bg-white p-6 shadow-sm">
            <h2 className="mb-6 text-2xl font-bold text-primary-800">
              Recent Sales
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[650px]">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="py-3">Invoice</th>
                    <th className="text-center">Customer</th>
                    <th className="text-center">Store</th>
                    <th className="text-center">Payment</th>
                    <th className="text-center">Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {recentTransactions.map((invoice) => (
                    <tr key={invoice.id} className="border-b hover:bg-gray-50">
                      <td className="py-4">{invoice.invoice}</td>
                      <td className="text-center">{invoice.customer}</td>
                      <td className="text-center">{invoice.store}</td>
                      <td className="text-center">{invoice.payment}</td>
                      <td className="text-center font-semibold">
                        {formatMoney(invoice.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
